import { supabase } from "./supabase";

export const productService = {
  // Get all products (for listing)
  async getProducts() {
    try {
      const { data, error } = await supabase.rpc("get_products_list");
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error fetching products:", error);
      return { success: false, error: error.message };
    }
  },

  // Get product details with all related data
  async getProductDetails(productId) {
    try {
      console.log("🔍 Getting product details for ID:", productId);

      const { data, error } = await supabase.rpc("get_product_details", {
        product_id: productId,
      });

      console.log("📊 RPC get_product_details result:", { data, error });

      if (error) throw error;

      console.log("✅ Product details retrieved successfully:", data);
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error fetching product details:", error);
      return { success: false, error: error.message };
    }
  },

  // Create a new product
  async createProduct(productData) {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("User not authenticated");

      // Convert marketing content to database format
      const marketingContentDb = productData.marketingContent
        ? {
            product_assets: {
              product_mockups_url:
                productData.marketingContent.productAssets?.productMockupsUrl ||
                null,
              raw_ugc_content_enabled:
                productData.marketingContent.productAssets
                  ?.rawUgcContentEnabled || false,
              training_documents_url:
                productData.marketingContent.productAssets
                  ?.trainingDocumentsUrl || null,
            },
            top_performing_examples: {
              top_image_ads: (
                productData.marketingContent.topPerformingExamples
                  ?.topImageAds || []
              ).map((ad) => ({
                image_url: ad.imageUrl || ad.image_url,
                title: ad.title,
                spend: ad.spend,
                roas: ad.roas,
              })),
              top_video_ads: (
                productData.marketingContent.topPerformingExamples
                  ?.topVideoAds || []
              ).map((ad) => ({
                video_url: ad.videoUrl || ad.video_url,
                title: ad.title,
                spend: ad.spend,
                roas: ad.roas,
              })),
              image_library_url:
                productData.marketingContent.topPerformingExamples
                  ?.imageLibraryUrl || null,
              video_library_url:
                productData.marketingContent.topPerformingExamples
                  ?.videoLibraryUrl || null,
              video_ads_enabled:
                productData.marketingContent.topPerformingExamples
                  ?.videoAdsEnabled || false,
            },
            high_performance_creatives: {
              videos: (
                productData.marketingContent.highPerformanceCreatives?.videos ||
                []
              ).map((video) => ({
                video_url: video.videoUrl || video.video_url,
                title: video.title,
                description: video.description,
                production_notes: {
                  ai_voice: video.productionNotes?.aiVoice || false,
                  ugc_clips: video.productionNotes?.ugcClips || false,
                  b_roll: video.productionNotes?.bRoll || false,
                  ai_deep_fake: video.productionNotes?.aiDeepFake || false,
                  product_b_roll: video.productionNotes?.productBRoll || false,
                  custom_notes:
                    video.productionNotes?.customNotes?.filter(
                      (note) => note.trim() !== ""
                    ) || [],
                },
              })),
              creative_library_url:
                productData.marketingContent.highPerformanceCreatives
                  ?.creativeLibraryUrl || null,
            },
            know_your_customer: {
              target_demographics: (
                productData.marketingContent.knowYourCustomer
                  ?.targetDemographics || []
              ).filter((item) => item.trim() !== ""),
              customer_persona: {
                name:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.name || null,
                age:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.age || null,
                location:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.location || null,
                income:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.income || null,
                job_title:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.jobTitle || null,
                reality:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.reality || null,
                fears:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.fears || null,
                hook:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.hook || null,
              },
            },
            pain_points_grid: productData.marketingContent.painPointsGrid || [],
            power_claims: productData.marketingContent.powerClaims || [],
            content_tools_access: {
              recharm_ugc_library: {
                enabled:
                  productData.marketingContent.contentToolsAccess
                    ?.recharmUgcLibrary?.enabled || false,
                instructions: {
                  website_url:
                    productData.marketingContent.contentToolsAccess
                      ?.recharmUgcLibrary?.instructions?.websiteUrl ||
                    "https://app.recharm.com/auth",
                  email:
                    productData.marketingContent.contentToolsAccess
                      ?.recharmUgcLibrary?.instructions?.email ||
                    "ched@mailinator.com",
                  inbox_url:
                    productData.marketingContent.contentToolsAccess
                      ?.recharmUgcLibrary?.instructions?.inboxUrl ||
                    "https://mailinator.com",
                },
                description:
                  productData.marketingContent.contentToolsAccess
                    ?.recharmUgcLibrary?.description ||
                  "Access to 150+ verified UGC clips from real customers. Content updated weekly with fresh testimonials and usage videos for male enhancement and energy benefits.",
              },
              research_analysis: {
                kalo_data_training: {
                  title:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.kaloDataTraining?.title ||
                    "Kalo Data Training",
                  description:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.kaloDataTraining?.description ||
                    "Learn to analyze trending content and competitor strategies for male enhancement and natural energy supplement niches.",
                  coming_soon:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.kaloDataTraining?.comingSoon ?? true,
                },
                competitor_intelligence: {
                  title:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.competitorIntelligence?.title ||
                    "Competitor Intelligence",
                  description:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.competitorIntelligence?.description ||
                    "Study what's working for other honey and male enhancement brands. Updated weekly with new findings.",
                  coming_soon:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.competitorIntelligence?.comingSoon ??
                    true,
                },
              },
            },
          }
        : {};

      // Insert main product
      const { data: product, error: productError } = await supabase
        .from("products")
        .insert({
          name: productData.name,
          description: productData.description,
          short_description: productData.shortDescription,
          main_image_url: productData.mainImageUrl,
          additional_images: productData.additionalImages || [],
          marketing_content: marketingContentDb,
          created_by: user.user.id,
          is_active: true,
        })
        .select()
        .single();

      if (productError) throw productError;

      // Insert pain points
      if (productData.painPoints && productData.painPoints.length > 0) {
        // Filter out empty pain points
        const validPainPoints = productData.painPoints.filter(
          (point) => point.painPoint && point.painPoint.trim() !== ""
        );

        if (validPainPoints.length > 0) {
          const painPointsData = validPainPoints.map((point, index) => ({
            product_id: product.id,
            pain_point: point.painPoint,
            psychological_trigger: point.psychologicalTrigger || "",
            ad_application: point.adApplication || "",
            sort_order: index,
          }));

          const { error: painError } = await supabase
            .from("product_pain_points")
            .insert(painPointsData);

          if (painError) throw painError;
        }
      }

      // Insert rules
      if (productData.rules && productData.rules.length > 0) {
        // Filter out empty rules
        const validRules = productData.rules.filter(
          (rule) => rule.ruleText && rule.ruleText.trim() !== ""
        );

        if (validRules.length > 0) {
          const rulesData = validRules.map((rule, index) => ({
            product_id: product.id,
            rule_text: rule.ruleText,
            sort_order: index,
          }));

          const { error: rulesError } = await supabase
            .from("product_rules")
            .insert(rulesData);

          if (rulesError) throw rulesError;
        }
      }

      // Insert links
      if (productData.links && productData.links.length > 0) {
        // Filter out empty links
        const validLinks = productData.links.filter(
          (link) => link.url && link.url.trim() !== ""
        );

        if (validLinks.length > 0) {
          const linksData = validLinks.map((link, index) => ({
            product_id: product.id,
            url: link.url,
            button_text: link.title || "",
            sort_order: index,
          }));

          const { error: linksError } = await supabase
            .from("product_links")
            .insert(linksData);

          if (linksError) throw linksError;
        }
      }

      // Handle brand association if specified
      if (productData.addToCurrentBrand && productData.brandId) {
        const { error: brandError } = await supabase
          .from("brand_products")
          .insert({
            brand_id: productData.brandId,
            product_id: product.id,
          });

        if (brandError) {
          console.error("Error associating product with brand:", brandError);
          // Don't throw error here, product was created successfully
          // Just log the warning
          console.warn("Product created but brand association failed:", brandError.message);
        }
      }

      return { success: true, data: product };
    } catch (error) {
      console.error("Error creating product:", error);
      return { success: false, error: error.message };
    }
  },

  // Update product
  async updateProduct(productId, productData) {
    try {
      console.log("🔄 Updating product:", productId, productData);

      // Convert marketing content to database format
      const marketingContentDb = productData.marketingContent
        ? {
            product_assets: {
              product_mockups_url:
                productData.marketingContent.productAssets?.productMockupsUrl ||
                null,
              raw_ugc_content_enabled:
                productData.marketingContent.productAssets
                  ?.rawUgcContentEnabled || false,
              training_documents_url:
                productData.marketingContent.productAssets
                  ?.trainingDocumentsUrl || null,
            },
            top_performing_examples: {
              top_image_ads: (
                productData.marketingContent.topPerformingExamples
                  ?.topImageAds || []
              ).map((ad) => ({
                image_url: ad.imageUrl || ad.image_url,
                title: ad.title,
                spend: ad.spend,
                roas: ad.roas,
              })),
              top_video_ads: (
                productData.marketingContent.topPerformingExamples
                  ?.topVideoAds || []
              ).map((ad) => ({
                video_url: ad.videoUrl || ad.video_url,
                title: ad.title,
                spend: ad.spend,
                roas: ad.roas,
              })),
              image_library_url:
                productData.marketingContent.topPerformingExamples
                  ?.imageLibraryUrl || null,
              video_library_url:
                productData.marketingContent.topPerformingExamples
                  ?.videoLibraryUrl || null,
              video_ads_enabled:
                productData.marketingContent.topPerformingExamples
                  ?.videoAdsEnabled || false,
            },
            high_performance_creatives: {
              videos: (
                productData.marketingContent.highPerformanceCreatives?.videos ||
                []
              ).map((video) => ({
                video_url: video.videoUrl || video.video_url,
                title: video.title,
                description: video.description,
                production_notes: {
                  ai_voice: video.productionNotes?.aiVoice || false,
                  ugc_clips: video.productionNotes?.ugcClips || false,
                  b_roll: video.productionNotes?.bRoll || false,
                  ai_deep_fake: video.productionNotes?.aiDeepFake || false,
                  product_b_roll: video.productionNotes?.productBRoll || false,
                  custom_notes:
                    video.productionNotes?.customNotes?.filter(
                      (note) => note.trim() !== ""
                    ) || [],
                },
              })),
              creative_library_url:
                productData.marketingContent.highPerformanceCreatives
                  ?.creativeLibraryUrl || null,
            },
            know_your_customer: {
              target_demographics: (
                productData.marketingContent.knowYourCustomer
                  ?.targetDemographics || []
              ).filter((item) => item.trim() !== ""),
              customer_persona: {
                name:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.name || null,
                age:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.age || null,
                location:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.location || null,
                income:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.income || null,
                job_title:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.jobTitle || null,
                reality:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.reality || null,
                fears:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.fears || null,
                hook:
                  productData.marketingContent.knowYourCustomer?.customerPersona
                    ?.hook || null,
              },
            },
            pain_points_grid: productData.marketingContent.painPointsGrid || [],
            power_claims: productData.marketingContent.powerClaims || [],
            content_tools_access: {
              recharm_ugc_library: {
                enabled:
                  productData.marketingContent.contentToolsAccess
                    ?.recharmUgcLibrary?.enabled || false,
                instructions: {
                  website_url:
                    productData.marketingContent.contentToolsAccess
                      ?.recharmUgcLibrary?.instructions?.websiteUrl ||
                    "https://app.recharm.com/auth",
                  email:
                    productData.marketingContent.contentToolsAccess
                      ?.recharmUgcLibrary?.instructions?.email ||
                    "ched@mailinator.com",
                  inbox_url:
                    productData.marketingContent.contentToolsAccess
                      ?.recharmUgcLibrary?.instructions?.inboxUrl ||
                    "https://mailinator.com",
                },
                description:
                  productData.marketingContent.contentToolsAccess
                    ?.recharmUgcLibrary?.description ||
                  "Access to 150+ verified UGC clips from real customers. Content updated weekly with fresh testimonials and usage videos for male enhancement and energy benefits.",
              },
              research_analysis: {
                kalo_data_training: {
                  title:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.kaloDataTraining?.title ||
                    "Kalo Data Training",
                  description:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.kaloDataTraining?.description ||
                    "Learn to analyze trending content and competitor strategies for male enhancement and natural energy supplement niches.",
                  coming_soon:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.kaloDataTraining?.comingSoon ?? true,
                },
                competitor_intelligence: {
                  title:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.competitorIntelligence?.title ||
                    "Competitor Intelligence",
                  description:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.competitorIntelligence?.description ||
                    "Study what's working for other honey and male enhancement brands. Updated weekly with new findings.",
                  coming_soon:
                    productData.marketingContent.contentToolsAccess
                      ?.researchAnalysis?.competitorIntelligence?.comingSoon ??
                    true,
                },
              },
            },
          }
        : {};

      // Update main product
      const { data: product, error: productError } = await supabase
        .from("products")
        .update({
          name: productData.name,
          description: productData.description,
          short_description: productData.shortDescription,
          main_image_url: productData.mainImageUrl,
          additional_images: productData.additionalImages || [],
          marketing_content: marketingContentDb,
          updated_at: new Date().toISOString(),
        })
        .eq("id", productId)
        .select()
        .single();

      console.log("📊 Product update result:", { product, productError });

      if (productError) throw productError;

      // Delete existing pain points, rules, and links
      console.log("🔄 Deleting existing related data...");
      const deleteResults = await Promise.all([
        supabase
          .from("product_pain_points")
          .delete()
          .eq("product_id", productId),
        supabase.from("product_rules").delete().eq("product_id", productId),
        supabase.from("product_links").delete().eq("product_id", productId),
      ]);

      console.log("📊 Delete results:", deleteResults);

      // Insert new pain points
      if (productData.painPoints && productData.painPoints.length > 0) {
        console.log("🔄 Inserting pain points:", productData.painPoints);

        // Filter out empty pain points
        const validPainPoints = productData.painPoints.filter(
          (point) => point.painPoint && point.painPoint.trim() !== ""
        );

        console.log("🔍 Valid pain points after filtering:", validPainPoints);

        if (validPainPoints.length > 0) {
          const painPointsData = validPainPoints.map((point, index) => ({
            product_id: productId,
            pain_point: point.painPoint,
            psychological_trigger: point.psychologicalTrigger || "",
            ad_application: point.adApplication || "",
            sort_order: index,
          }));

          const { error: painError } = await supabase
            .from("product_pain_points")
            .insert(painPointsData);

          if (painError) throw painError;
          console.log("✅ Pain points inserted successfully");
        } else {
          console.log("⚠️ No valid pain points to insert");
        }
      }

      // Insert new rules
      if (productData.rules && productData.rules.length > 0) {
        console.log("🔄 Inserting rules:", productData.rules);

        // Filter out empty rules
        const validRules = productData.rules.filter(
          (rule) => rule.ruleText && rule.ruleText.trim() !== ""
        );

        console.log("🔍 Valid rules after filtering:", validRules);

        if (validRules.length > 0) {
          const rulesData = validRules.map((rule, index) => ({
            product_id: productId,
            rule_text: rule.ruleText,
            sort_order: index,
          }));

          const { error: rulesError } = await supabase
            .from("product_rules")
            .insert(rulesData);

          if (rulesError) throw rulesError;
          console.log("✅ Rules inserted successfully");
        } else {
          console.log("⚠️ No valid rules to insert");
        }
      }

      // Insert new links
      if (productData.links && productData.links.length > 0) {
        console.log("🔄 Inserting links:", productData.links);

        // Filter out empty links
        const validLinks = productData.links.filter(
          (link) => link.url && link.url.trim() !== ""
        );

        console.log("🔍 Valid links after filtering:", validLinks);

        if (validLinks.length > 0) {
          const linksData = validLinks.map((link, index) => ({
            product_id: productId,
            url: link.url,
            button_text: link.title || "",
            sort_order: index,
          }));

          const { error: linksError } = await supabase
            .from("product_links")
            .insert(linksData);

          if (linksError) throw linksError;
          console.log("✅ Links inserted successfully");
        } else {
          console.log("⚠️ No valid links to insert");
        }
      }

      console.log("✅ Product updated successfully:", product);
      return { success: true, data: product };
    } catch (error) {
      console.error("❌ Error updating product:", error);
      return { success: false, error: error.message };
    }
  },

  // Delete product
  async deleteProduct(productId) {
    try {
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", productId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("Error deleting product:", error);
      return { success: false, error: error.message };
    }
  },

  // Toggle product active status
  async toggleProductStatus(productId, isActive) {
    try {
      const { data, error } = await supabase
        .from("products")
        .update({ is_active: isActive })
        .eq("id", productId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error toggling product status:", error);
      return { success: false, error: error.message };
    }
  },

  // Get all products for admin (including inactive)
  async getAdminProducts() {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Error fetching admin products:", error);
      return { success: false, error: error.message };
    }
  },

  // Upload image to Supabase Storage
  async uploadImage(file, folder = "products") {
    try {
      console.log("📤 Uploading image:", {
        fileName: file.name,
        fileSize: file.size,
        folder,
      });

      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;

      console.log("🔄 Upload path:", filePath);

      const { data, error } = await supabase.storage
        .from("product-images")
        .upload(filePath, file);

      console.log("📊 Storage upload result:", { data, error });

      if (error) throw error;

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("product-images").getPublicUrl(filePath);

      console.log("✅ Image uploaded successfully:", {
        path: filePath,
        url: publicUrl,
      });
      return { success: true, data: { path: filePath, url: publicUrl } };
    } catch (error) {
      console.error("❌ Error uploading image:", error);
      return { success: false, error: error.message };
    }
  },

  // Delete image from Supabase Storage
  async deleteImage(filePath) {
    try {
      const { error } = await supabase.storage
        .from("product-images")
        .remove([filePath]);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("Error deleting image:", error);
      return { success: false, error: error.message };
    }
  },
};
